import { UserDisplay } from "@components/common/user-display";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { SdbTeam } from "@custom-types/teams-types";
import { Dialog, Text, useToast } from "@flat-ui";
import { useCompanyId } from "@context-providers/company-id-provider";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { convertToTeamMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { deleteTeam } from "@store/teams/teams-slice-thunk";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** The team to remove */
  team: SdbTeam;

  /** Callback invoked when the dialog is closed (cancel or after successful removal) */
  onClose: () => void;
}

/**
 * Confirmation dialog for removing a team from the workspace.
 * Handles tracking, dispatching the delete, and showing a success toast.
 */
export function RemoveTeamDialog({ team, onClose }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { isRemovingTeam } = useAppSelector(fetchingTeamsFlagsSelector);

  async function removeTeam(): Promise<void> {
    trackEvent({
      name: TeamEvents.deleteTeam,
      props: { numberOfMembers: team.memberCount },
    });

    try {
      await dispatch(deleteTeam({ companyId, teamId: team.id })).unwrap();

      openToast({
        title: `${capitalizeFirstLetter(
          TEAM_DISPLAY_NAME
        )} deleted successfully`,
      });
      onClose();
    } catch {
      // Errors are handled by slice
    }
  }

  return (
    <Dialog
      title={`Remove ${TEAM_DISPLAY_NAME}?`}
      confirmText="Remove"
      open
      onConfirm={removeTeam}
      onCancel={onClose}
      showSpinner={isRemovingTeam}
    >
      <Text
        variant="bodyM"
        sx={{
          marginBottom: SPACE_ELEMENTS_OF_MODAL,
        }}
      >
        This action will only remove the selected {TEAM_DISPLAY_NAME}. All the
        members of the {TEAM_DISPLAY_NAME} will remain in the workspace.
      </Text>

      <UserDisplay member={convertToTeamMemberTypes(team)} />
    </Dialog>
  );
}
